import { FC, useRef } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { BrandAdToken } from "../../../shared/models/brand.model";
import { adTokenLabel } from "../../../shared/adTokens/adTokens";
import type { BrandAdTokenValueEntry } from "../hooks/useOrganizationBrand";

/*
    The Ad Token Values section of the Branding tab behind `brand-identity-v2` (MPN-2030).

    Works like a Django admin inline (plan D11): empty until someone adds a row. "+ Add" picks
    an ad token from the ones this brand has no value for yet and takes a free-text value, e.g.
    [ADVISOR] → "Adviser". A saved row's ad token is fixed; only its value changes. Removing a
    row archives the value on save.
*/

interface Props {
  values: BrandAdTokenValueEntry[];
  setValues: (update: (prev: BrandAdTokenValueEntry[]) => BrandAdTokenValueEntry[]) => void;
  /** The ad tokens the server says this brand can still take a value for. */
  availableAdTokens: BrandAdToken[];
  onSave: () => Promise<void>;
  isLoading: boolean;
}

/** An ad token as ad copy spells it, brackets and all: [ADVISOR]. */
const bracketed = (adToken: BrandAdToken) => `[${adTokenLabel(adToken.token)}]`;

const BrandAdTokenValuesSection: FC<Props> = ({ values, setValues, availableAdTokens, onSave, isLoading }) => {
  const nextKey = useRef(0);

  // A brand holds one value per ad token, so a token picked on one added row is gone from the others.
  const choicesFor = (key?: string) => {
    const picked = values.filter((v) => !v.uuid && v.key !== key).map((v) => v.ad_token_definition.uuid);
    return availableAdTokens.filter((t) => !picked.includes(t.uuid));
  };
  const unpicked = choicesFor();

  const change = (key: string, update: Partial<BrandAdTokenValueEntry>) => {
    setValues((prev) => prev.map((v) => (v.key === key ? { ...v, ...update } : v)));
  };

  const addValue = () => {
    nextKey.current += 1;
    const entry = { key: `new-${nextKey.current}`, uuid: null, ad_token_definition: unpicked[0], value: "" };
    setValues((prev) => [...prev, entry]);
  };

  const removeValue = (key: string) => {
    setValues((prev) => prev.filter((v) => v.key !== key));
  };

  return (
    <div className="brand-section">
      <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
        <div>
          <h6 className="brand-section-title">Ad Token Values</h6>
          <p className="brand-section-hint">
            What this brand's ad tokens stand for in ad copy, e.g. [ADVISOR] → "Adviser".
          </p>
        </div>
        <span className="brand-section-count">
          {values.length} {values.length === 1 ? "value" : "values"}
        </span>
      </div>

      {values.length === 0 && availableAdTokens.length === 0 && (
        <div className="text-center text-muted small py-3">No ad tokens are available to set yet.</div>
      )}

      {values.map((entry) => {
        const label = bracketed(entry.ad_token_definition);
        return (
          <div key={entry.key} className="brand-font-row px-3 py-2 mb-2" role="group" aria-label={label}>
            <Row className="align-items-start">
              <Col md={4}>
                {entry.uuid ? (
                  <div className="fw-semibold py-2">{label}</div>
                ) : (
                  <Form.Select
                    aria-label="Ad token"
                    value={entry.ad_token_definition.uuid}
                    onChange={(e) =>
                      change(entry.key, { ad_token_definition: availableAdTokens.find((t) => t.uuid === e.target.value) })
                    }
                  >
                    {choicesFor(entry.key).map((t) => (
                      <option key={t.uuid} value={t.uuid}>
                        {bracketed(t)}
                      </option>
                    ))}
                  </Form.Select>
                )}
              </Col>
              <Col md={7}>
                <Form.Control
                  type="text"
                  aria-label={`${label} value`}
                  value={entry.value}
                  onChange={(e) => change(entry.key, { value: e.target.value })}
                />
                {entry.ad_token_definition.help_text && (
                  <div className="small text-muted mt-1">{entry.ad_token_definition.help_text}</div>
                )}
              </Col>
              <Col md={1} className="d-flex justify-content-end">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger mt-1"
                  aria-label={`Remove ${label}`}
                  onClick={() => removeValue(entry.key)}
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              </Col>
            </Row>
          </div>
        );
      })}

      <div className="d-flex justify-content-between mt-3">
        <Button
          variant="outline-primary"
          size="sm"
          onClick={addValue}
          disabled={isLoading || unpicked.length === 0}
          title={unpicked.length === 0 ? "Every ad token this brand can take already has a value" : undefined}
        >
          <i className="bi bi-plus-lg me-1"></i>
          Add
        </Button>
        <Button variant="primary" size="sm" onClick={() => onSave()} disabled={isLoading}>
          Save Ad Token Values
        </Button>
      </div>
    </div>
  );
};

export default BrandAdTokenValuesSection;
